import {
  Autocomplete as MuiAutocomplete,
  Box,
  Checkbox,
  Grid,
  MenuItem,
  Typography,
} from '@mui/material';
import { get } from 'lodash-es';
import states from '../../../lib/usStates';
import { useContext } from './context';
import { TextField } from '../../../components/inputs/TextField';
import ErrorMessage from '../../../components/ErrorMessage';
import { useFeatureFlag } from '../../../hooks/use-feature-flag';
import { PermissionsPreview } from './PermissionsPreview';

export function Form() {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <Error />
      <Fields />
    </Box>
  );
}

function Header() {
  const { isNew } = useContext();
  return (
    <>
      <Typography variant="h4">{isNew ? 'New User' : 'Edit User'}</Typography>
      <Typography variant="caption">
        {`${isNew ? 'Add' : 'Edit'} User Information using the text fields below`}
      </Typography>
    </>
  );
}

function Error() {
  const { error } = useContext();
  if (!error) return null;
  return <ErrorMessage sx={{ mt: 2, mx: { md: 2 } }} error={error} />;
}

function Fields() {
  const isSignalOn = useFeatureFlag('Signal');

  return (
    <>
      <Grid container sx={{ mt: 2, px: { md: 2 } }}>
        <RequiredFields xs={12} md={6} sx={{ pr: { md: 5 } }} />
        <OptionalFields
          xs={12}
          md={6}
          sx={{
            mt: { xs: 3, md: 0 },
            pl: { md: 5 },
            borderLeft: { md: '1px solid rgba(0, 0, 0, 0.12)' },
          }}
        />
      </Grid>
      {isSignalOn ? <PermissionsPreview sx={{ mt: 4, px: { md: 2 } }} /> : null}
    </>
  );
}

function RolesSelector(props) {
  const { roles } = useContext();
  const isSignalOn = useFeatureFlag('Signal');

  return (
    <Autocomplete
      {...props}
      tabIndex={7}
      label="Roles"
      name="roles"
      multiple={isSignalOn}
      getOptionLabel={(r) => (Array.isArray(r) ? r[0]?.label : r.label)}
      options={roles}
    />
  );
}

function RequiredFields(props) {
  const { farmgate, organizations, isNew } = useContext();

  return (
    <Grid item {...props}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Required Fields
      </Typography>

      <Grid container spacing={{ xs: 3, sm: 5 }}>
        <Grid container item direction="column" xs={12} sm={6} spacing={3}>
          <Input
            tabIndex={1}
            disabled={!isNew}
            label="Username"
            name="username"
          />
          <Input tabIndex={2} label="First Name" name="firstName" />
          <Input tabIndex={3} label="Last Name" name="lastName" />
          <Input tabIndex={4} label="Email" name="email" />
          <FarmgateSelect
            tabIndex={9}
            label="Region"
            name="regionId"
            options={farmgate.regions.map(mapName)}
          />
          <FarmgateSelect
            tabIndex={10}
            label="Regional Business Director"
            name="regionalBusinessDirector"
            options={farmgate.regionalBusinessDirectors.map(mapPerson)}
          />
          <FarmgateSelect
            tabIndex={11}
            label="Area Business Manager"
            name="areaBusinessManager"
            options={farmgate.areaBusinessManagers.map(mapPerson)}
          />
        </Grid>
        <Grid container item direction="column" xs={12} sm={6} spacing={3}>
          <Input
            tabIndex={5}
            label="Phone"
            name="phone"
            mask="+{1} (#00) 000-0000"
          />
          <RolesSelector tabIndex={6} />
          <Autocomplete
            tabIndex={7}
            label="Organization"
            name="organizations"
            getOptionLabel={(o) => o.name}
            options={organizations}
          />
          <FarmgateSelect
            tabIndex={8}
            label="FarmGate Role"
            name="groupId"
            options={farmgate.roles.map(mapName)}
          />
          <FarmgateInput
            tabIndex={12}
            label="Yearly Business Goal"
            name="yearlyBusinessGoal"
          />
          <FarmgateAutocomplete
            tabIndex={13}
            label="Working Relationships"
            name="partners"
            getOptionLabel={(u) => `${u.firstName} ${u.lastName}`}
            options={farmgate.agronomists}
            disableCloseOnSelect
          />
        </Grid>
      </Grid>
    </Grid>
  );
}

function OptionalFields(props) {
  const { user } = useContext();

  return (
    <Grid item {...props}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Optional Fields
      </Typography>

      <Grid container spacing={{ xs: 3, sm: 5 }}>
        <Grid container item direction="column" xs={12} sm={6} spacing={3}>
          <Input tabIndex={22} label="Address Line 1" name="addressLine1" />
          <Input tabIndex={24} label="City" name="city" />
          <Input tabIndex={26} label="Zip" name="zip" />
        </Grid>
        <Grid container item direction="column" xs={12} sm={6} spacing={3}>
          <Input tabIndex={23} label="Address Line 2" name="addressLine2" />
          <Select
            tabIndex={25}
            label="State"
            name="state"
            options={states.map(mapState)}
          />
        </Grid>
      </Grid>

      {user.ccaLicenses?.length ? (
        <>
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            CCA Licenses
          </Typography>
          <Grid container rowSpacing={3} columnSpacing={{ xs: 3, sm: 5 }}>
            {user.ccaLicenses.map((license) => (
              <>
                <Grid item xs={12} sm={6} sx={{ width: '100%' }}>
                  <TextField
                    disabled
                    fullWidth
                    value={license.licenseNumber}
                    label="License Number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ width: '100%' }}>
                  <TextField
                    disabled
                    fullWidth
                    value={license.state}
                    label="State"
                  />
                </Grid>
              </>
            ))}
          </Grid>
        </>
      ) : null}
    </Grid>
  );
}

function mapState(state) {
  return (
    <MenuItem key={state.abbreviation} value={state.abbreviation}>
      {state.name}
    </MenuItem>
  );
}

function mapPerson(person) {
  return (
    <MenuItem key={person.id} value={person.id}>
      {person.firstName} {person.lastName}
    </MenuItem>
  );
}

function mapName(thing) {
  return (
    <MenuItem key={thing.id} value={thing.id}>
      {thing.name}
    </MenuItem>
  );
}

function FarmgateSelect({ options, ...props }) {
  return (
    <FarmgateInput {...props} select>
      {options}
    </FarmgateInput>
  );
}

function FarmgateInput(props) {
  const { user } = useContext();
  if (!user.isAgronomist) return null;
  return <Input {...props} name={`farmgate.${props.name}`} />;
}

function Select({ options, ...props }) {
  return (
    <Input {...props} select>
      {options}
    </Input>
  );
}

function Input({ name, ...passThrough }) {
  const { user, updateUser, error } = useContext();

  return (
    <Grid item sx={{ width: '100%' }}>
      <TextField
        fullWidth
        name={name}
        value={get(user, name)}
        onChange={(e) => updateUser(e.target)}
        error={get(error?.validationErrors, name)?.message}
        {...passThrough}
      />
    </Grid>
  );
}

function FarmgateAutocomplete({ name, ...props }) {
  const { user } = useContext();
  if (!user.isAgronomist) return null;
  return <Autocomplete name={`farmgate.${name}`} {...props} />;
}

function Autocomplete({
  name,
  options,
  disableCloseOnSelect,
  getOptionLabel,
  multiple = true,
  ...passThrough
}) {
  const { user, updateUser } = useContext();
  const currentValue = get(user, name);

  return (
    <Grid
      item
      sx={{
        width: '100%',
        '& .MuiAutocomplete-tag': {
          maxWidth: 'calc(100% - 45px)',
        },
      }}
    >
      <MuiAutocomplete
        options={options}
        fullWidth
        disableCloseOnSelect={disableCloseOnSelect}
        disableClearable
        multiple={multiple}
        name={name}
        value={multiple ? currentValue : currentValue[0] || null}
        onChange={(e, value) => updateUser({ name, value })}
        getOptionKey={(option) => option.id}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={getOptionLabel}
        renderOption={({ key, ...optionProps }, option, { selected }) => (
          <li key={key} {...optionProps}>
            <Checkbox checked={selected} />
            {getOptionLabel(option)}
          </li>
        )}
        renderInput={(renderProps) => (
          <TextField {...renderProps} {...passThrough} />
        )}
      />
    </Grid>
  );
}
